
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  maxDuration?: number; // max duration in seconds
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function TimePicker({ 
  value, 
  onChange, 
  maxDuration = 3600, 
  disabled = false,
  placeholder = "00:00",
  className
}: TimePickerProps) {
  const [localValue, setLocalValue] = useState<string>(value || "00:00");
  const [isValid, setIsValid] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  // Format seconds to mm:ss
  const formatTimeFromSeconds = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Parse mm:ss to seconds
  const parseTimeToSeconds = (timeStr: string): number => {
    const [mins, secs] = timeStr.split(':').map(Number);
    return (mins * 60) + secs;
  };

  // Validate and format input
  const validateTime = (input: string): boolean => {
    // Check format
    if (!/^\d{1,2}:\d{2}$/.test(input)) {
      return false;
    }

    const seconds = parseTimeToSeconds(input);
    
    // Check if within valid range
    if (seconds < 0 || (maxDuration && seconds > maxDuration)) {
      return false;
    }

    return true;
  };

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    
    // Handle backspace and direct editing
    if (input.length <= 5) {
      setLocalValue(input);
    }
    
    // Validate and update parent if valid
    if (validateTime(input)) {
      setIsValid(true);
      onChange(input);
    } else {
      setIsValid(false);
    }
  };

  // Auto-format when typing
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget.value;
    
    // Add colon automatically after 2 digits for minutes
    if (input.length === 2 && !input.includes(':') && e.key !== 'Backspace') {
      setLocalValue(`${input}:`);
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
  };

  // Handle blur to format correctly
  const handleBlur = () => {
    if (!localValue || !validateTime(localValue)) {
      // Reset to valid default or previous value
      setLocalValue(value || "00:00");
      setIsValid(true);
    } else {
      // Ensure proper formatting (e.g., "1:5" becomes "01:05")
      const [mins, secs] = localValue.split(':');
      const formattedValue = `${mins.padStart(2, '0')}:${secs.padStart(2, '0')}`;
      setLocalValue(formattedValue);
      onChange(formattedValue);
    }
    setIsEditing(false);
  };

  // Sync with parent value only when not editing
  useEffect(() => {
    if (!isEditing && value && value !== localValue && validateTime(value)) {
      setLocalValue(value);
      setIsValid(true);
    }
  }, [value, isEditing]);

  return (
    <Input
      type="text"
      value={localValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={cn(`w-24 text-center ${!isValid ? 'border-red-500' : ''}`, className)}
      maxLength={5}
    />
  );
}
